import { useMutation, useQuery } from '@apollo/client'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import type { LeaderGroupForm, LeaderGroupNode } from '@/modules/companies'
import {
  OldAddButton,
  ALL_LEADER_GROUPS,
  CREATE_LEADER_GROUP,
  DELETE_LEADER_GROUP,
  Header,
  PATCH_LEADER_GROUP,
} from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { useTranslate } from '@ur-solutions/react-hooks'
import { getNodes } from '@/utils/graphql'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { EditLeaderGroupPrompt } from './EditLeaderGroupPrompt'
import { PERMISSIONS } from '@/utils/permissions'
import { useMemo } from 'react'

const Wrapper = styled(Card)`
  margin: 1rem 0;
  > p {
    margin: 0 0 0 0.5rem;
  }
`

const H2 = styled.h2`
  display: flex;

  margin: 1rem 0 0 8px;
  font-weight: 600;

  div {
    margin-left: 1rem;
  }
`

interface LeaderGroupsProps {}

export const LeaderGroups: React.FC<LeaderGroupsProps> = () => {
  const translations = useTranslate({
    leaderGroups: 'companies.leader-groups',
    leaderGroupsDescription: 'companies.leader-groups-description',

    name: 'common.name',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteLeaderGroup: 'companies.delete-leader-group',
    deleteLeaderGroupConfirm: 'companies.delete-leader-group-confirm',

    toast: {
      successCreate: 'companies.leader-group-create-success',
      successUpdate: 'companies.leader-group-update-success',
      successDelete: 'companies.leader-group-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_LEADER_GROUPS)

  const { data: userData, loading: loadingUsers } = useQuery(
    SIMPLE_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        includeInactive: false,
      },
    }
  )

  const companyUsers = useMemo(
    () =>
      getNodes(userData?.allUsers).map(user => ({
        id: user.id,
        fullName: user.fullName,
      })) ?? [],
    [userData]
  )

  const refetchQueries = [
    {
      query: ALL_LEADER_GROUPS,
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createLeaderGroup] = useMutation(CREATE_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [patchLeaderGroup] = useMutation(PATCH_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteLeaderGroup] = useMutation(DELETE_LEADER_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
    },
  })

  const leaderGroups = useNodes(data?.allLeaderGroups)

  const handleAdd = async () => {
    const { data } = await addPrompt<LeaderGroupForm | null>(resolve => (
      <EditLeaderGroupPrompt onSubmit={resolve} companyUsers={companyUsers} />
    ))

    if (!data) return

    void createLeaderGroup({
      variables: {
        input: {
          name: data.name,
          users: data.users,
          leaders: data.leaders,
          salaryGroup: data.salaryGroup,
        },
      },
    })
  }

  const handleEdit = async (leaderGroup: LeaderGroupNode) => {
    const initialData = {
      name: leaderGroup.name,
      users: leaderGroup.users.map(user => user.id),
      leaders: leaderGroup.leaders.map(user => user.id),
      salaryGroup: leaderGroup.salaryGroup?.id ?? null,
    }
    const { data } = await addPrompt<LeaderGroupForm | null>(resolve => (
      <EditLeaderGroupPrompt
        initialData={initialData}
        companyUsers={companyUsers}
        onSubmit={resolve}
      />
    ))

    if (!data) return

    void patchLeaderGroup({
      variables: {
        id: leaderGroup.id,
        input: {
          name: data.name,
          users: data.users,
          leaders: data.leaders,
          salaryGroup: data.salaryGroup,
        },
      },
    })
  }

  const handleDelete = async (leaderGroup: LeaderGroupNode) => {
    const { data } = await confirm(
      translations.deleteLeaderGroupConfirm,
      translations.deleteLeaderGroup
    )

    if (!data) return

    void deleteLeaderGroup({
      variables: {
        id: leaderGroup.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <Header>
        <H2>{translations.leaderGroups}</H2>
        {loading && <Loader.Spinner />}
        {!loading && !loadingUsers && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </Header>
      <p>{translations.leaderGroupsDescription}</p>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.name}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {leaderGroups.map(leaderGroup => (
              <tr key={leaderGroup.id}>
                <td>{leaderGroup.name}</td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(leaderGroup as LeaderGroupNode)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() =>
                        handleDelete(leaderGroup as LeaderGroupNode)
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
